import React from 'react';
import { CreditCard, Users, Settings, Home, CheckCircle } from 'lucide-react';
import { features } from '../data/mock';

const iconMap = {
  CreditCard,
  Users,
  Settings,
  Home
};

export const FeaturesSection = () => {
  return (
    <section id="features" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#6366F1]/10 text-[#6366F1] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Settings className="h-4 w-4" />
            <span>Powerful Features</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Everything You Need to
            <span className="block text-[#6366F1]">Manage Your PG</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our comprehensive platform streamlines every aspect of PG management, 
            from rent collection to tenant satisfaction.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
          {features.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            const isEven = index % 2 === 0;
            
            return (
              <div
                key={feature.id}
                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8`}
              >
                {/* Content */}
                <div className={`flex-1 space-y-6 ${isEven ? 'lg:pr-8' : 'lg:pl-8'}`}>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-[#6366F1]/10 rounded-xl flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-[#6366F1]" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {feature.title}
                      </h3>
                    </div>
                    
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>

                  {/* Benefits List */}
                  <div className="space-y-3">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Visual Element */}
                <div className="flex-1 w-full">
                  <div className="relative">
                    {/* Background Shape */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${
                      index === 0 ? 'from-blue-500/20 to-[#6366F1]/20' :
                      index === 1 ? 'from-green-500/20 to-teal-500/20' :
                      index === 2 ? 'from-orange-500/20 to-red-500/20' :
                      'from-purple-500/20 to-pink-500/20'
                    } rounded-3xl transform rotate-3`}></div>
                    
                    {/* Content Card */}
                    <div className="relative bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                      {/* Feature Preview */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="text-lg font-semibold text-gray-900">
                            {feature.title.split(' ')[0]} {feature.title.split(' ')[1]}
                          </div>
                          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        </div>
                        
                        {/* Mock Interface Elements */}
                        {index === 0 && (
                          <div className="space-y-3">
                            <div className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600">Room 101 - John Doe</span>
                              <span className="text-sm font-semibold text-green-600">₹8,000 Paid</span>
                            </div>
                            <div className="flex justify-between items-center py-2 px-3 bg-yellow-50 rounded-lg">
                              <span className="text-sm text-gray-600">Room 102 - Jane Smith</span>
                              <span className="text-sm font-semibold text-yellow-600">₹8,000 Due</span>
                            </div>
                          </div>
                        )}
                        
                        {index === 1 && (
                          <div className="space-y-3">
                            <div className="flex items-center space-x-3 py-2">
                              <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">Amit Sharma</div>
                                <div className="text-xs text-gray-500">Room 105 • Active</div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3 py-2">
                              <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">Priya Patel</div>
                                <div className="text-xs text-gray-500">Room 108 • Pending</div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {index === 2 && (
                          <div className="space-y-3">
                            <div className="py-2 px-3 bg-red-50 rounded-lg">
                              <div className="text-sm font-medium text-red-900">AC Repair - Room 203</div>
                              <div className="text-xs text-red-600">High Priority • 2 hours ago</div>
                            </div>
                            <div className="py-2 px-3 bg-green-50 rounded-lg">
                              <div className="text-sm font-medium text-green-900">Plumbing Fixed - Room 101</div>
                              <div className="text-xs text-green-600">Completed • Yesterday</div>
                            </div>
                          </div>
                        )}
                        
                        {index === 3 && (
                          <div className="grid grid-cols-3 gap-2">
                            {Array.from({ length: 9 }).map((_, i) => (
                              <div
                                key={i}
                                className={`aspect-square rounded-lg flex items-center justify-center text-xs font-medium ${
                                  i < 7 ? 'bg-green-100 text-green-800' :
                                  i === 7 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-500'
                                }`}
                              >
                                {i + 1}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Floating Icon */}
                    <div className="absolute -top-3 -right-3 bg-white rounded-full p-3 shadow-lg border border-gray-100">
                      <IconComponent className="h-5 w-5 text-[#6366F1]" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};